/**
 * The walkers' jumps (02 §5 Zen-chan): the vertical jump (16-tick crouch, 41 px up through
 * platforms, back down landing on the way) and the gap jump (15 px up and down while drifting 2 px
 * a tick). Ported from D_EBC4, D_EBD9, L_EC0C and D_EC87.
 */

import { hit, hitRow, OFF, type Probe } from './map';
import { animate } from './motion';
import { CROUCH_START, DESCENT_STEPS, GAP_TABLE, JUMP_TABLE, LINE_Y, MAX_X, TOP_Y, WALK_STEP } from './tuning';
import type { Monster, World } from '../types';

const drifting = (m: Monster): boolean => m.ai.driftL !== 0 || m.ai.driftR !== 0;

/** D_EBC4: start a vertical jump (no drift, jump request cleared, counter $1F) and run its first tick. */
export function startVerticalJump(world: World, m: Monster, p: Probe): void {
  const a = m.ai;
  a.driftL = 0;
  a.driftR = 0;
  a.joy &= 0xfb;
  a.jump = CROUCH_START;
  jumpTick(world, m, p);
}

/** D_EBD9: one tick of a jump, by its counter. */
export function jumpTick(world: World, m: Monster, p: Probe): void {
  const a = m.ai;
  const n = a.jump;
  if (n === 0) {
    descentTick(world, m, p);
    return;
  }
  a.jump = n - 1;
  if (n >= 0x10) {
    // Crouching: every 4th tick it looks the other way (L_EBFB `eor D_8750,x`); no animation.
    if ((n & 3) === 0) a.frame ^= a.faceFrames;
    return;
  }
  if (drifting(m)) {
    m.y = (m.y - 1) & 0xff;
    drift(world, m, p);
  } else {
    m.y = (m.y - (JUMP_TABLE[n] ?? 0)) & 0xff;
  }
  animate(m);
}

/** D_EC7C: the jump is over. The drift flags stay as they are (the C64 leaves them). */
function endJump(m: Monster): void {
  m.ai.jump = -1;
  m.ai.descent = -1;
}

/** L_EC0C: coming down, landing on the first platform whose top it crosses (one-way, as falling). */
function descentTick(world: World, m: Monster, p: Probe): void {
  const a = m.ai;
  if (a.descent < 0) a.descent = 0;
  const step = a.descent;
  if (step === DESCENT_STEPS) {
    endJump(m);
    animate(m);
    return;
  }
  a.descent++;
  m.y = (m.y + ((drifting(m) ? GAP_TABLE[step] : JUMP_TABLE[step]) ?? 0)) & 0xff;
  // D_EC3C: an 8-px line was crossed when the sub-row wrapped below the one it started the tick on.
  const crossed = m.y >= TOP_Y && ((m.y - 0x15) & 7) < p.subY;
  if (crossed && !hitRow(world, p, OFF.floor) && hitRow(world, p, OFF.below)) {
    m.y = ((((m.y - LINE_Y) & 0xf8) + LINE_Y) & 0xff);
    endJump(m);
    animate(m);
    return;
  }
  drift(world, m, p);
  animate(m);
}

/**
 * D_EC87: a gap jump's sideways drift, 2 px a tick. On the column grid it checks the column ahead
 * beside its lowest body row; a wall there (unless it is already inside one) stops the drift for
 * the rest of the jump. Drifting right also stops at X $F4.
 */
function drift(world: World, m: Monster, p: Probe): void {
  const a = m.ai;
  if (a.driftL < 0) {
    const off = p.subY === 0 ? OFF.lowLeft : OFF.feetLeft;
    if (p.subX !== 0 || !hit(world, p, off) || hit(world, p, off + 1)) m.x = (m.x - WALK_STEP) & 0xff;
    else a.driftL = 1;
    return;
  }
  if (a.driftR >= 0) return;
  if (m.x === MAX_X) {
    a.driftR = 1;
    return;
  }
  const off = p.subY === 0 ? OFF.lowRight : OFF.feetRight;
  if (p.subX !== 0 || !hit(world, p, off) || hit(world, p, off - 1)) m.x = (m.x + WALK_STEP) & 0xff;
  else a.driftR = 1;
}
